<template>
  <v-main class="paywall-detail-page">
    <v-app-bar app flat light color="white">
      <AppbarBackIcon />
      <v-toolbar-title class="font-weight-bold">
        <span>付费界面</span>
        <span v-if="id"></span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn v-if="!isCreate" color="blue" class="ml-4" outlined small @click="onPreview">预览</v-btn>
      <v-btn v-if="!isCreate" color="red" class="ml-4" outlined small @click="onDelete">删除</v-btn>
      <PaywallPreviewDialog :url="previewUrl" v-model="showPreview" />
    </v-app-bar>
    <v-container fluid>
      <v-row dense v-if="!isCreate && paywall">
        <v-col cols="12">
          <v-text-field label="付费界面ID" :value="paywall.id" readonly></v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field label="名称" hint="例如：我的会员" class="required" v-model="form.name" />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field label="别名" hint="用于代码中引用，例如：paywall_vip" v-model="form.alias" />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-textarea label="介绍文案" hint="显示在付费界面上，支持Markdown/HTML格式" placeholder="支持Markdown/HTML格式"
            v-model="form.intro_md" />
        </v-col>
      </v-row>
      <SelectList v-model="form.equity_group_id_s" label="权益组" title="权益展示" :options="equity_group_option_s"></SelectList>
      <SelectList v-model="form.product_id_s" label="商品" title="商品展示" :options="product_option_s"></SelectList>
      <v-expansion-panels class="section-payment" focusable>
        <SelectList v-for="deviceItem in device_type_list" :key="deviceItem.device_type"
          v-model="form.device_channel_id_s[deviceItem.device_type]" expansion label="支付方式"
          :title="`支付方式 - ${deviceItem.name}`" :options="payment_channel_option_s"></SelectList>
      </v-expansion-panels>
      <v-btn width="100%" color="primary" class="mt-16" @click="onSave">保存</v-btn>
    </v-container>
  </v-main>
</template>

<script>
import _ from 'lodash'
import AppbarBackIcon from '@/components/AppbarBackIcon.vue'
import SelectList from '@/components/SelectList.vue'
import { homeStore } from '@/store/home'
import { paymentChannelStore } from '@/store/paymentChannel'
import { paywallStore } from '@/store/paywall'
import { productStore } from '@/store/product'
import { equityStore } from '@/store/equity'
import { customerStore } from '@/store/customer'
import { PaymentDeviceType } from '@/plugins/payment'
import PaywallPreviewDialog from '@/views/product/PaywallPreviewDialog.vue'

function filterNil(list) {
  return list.filter(x => !_.isNil(x))
}

const DEVICE_TYPE_LIST = [
  { device_type: String(PaymentDeviceType.DESKTOP), name: '电脑端' },
  { device_type: String(PaymentDeviceType.MOBILE), name: '手机网站' },
  { device_type: String(PaymentDeviceType.APP), name: '手机APP' },
  { device_type: String(PaymentDeviceType.WEIXIN), name: '手机微信' },
]

export default {
  name: 'PaywallDetailPage',
  components: { AppbarBackIcon, SelectList, PaywallPreviewDialog },
  data() {
    let form = {
      name: '',
      alias: '',
      intro_md: '',
      equity_group_id_s: [],
      product_id_s: [],
      device_channel_id_s: {},
    }
    DEVICE_TYPE_LIST.forEach(item => {
      form.device_channel_id_s[String(item.device_type)] = []
    })
    return {
      previewUrl: null,
      showPreview: false,
      form: form,
    }
  },
  computed: {
    id() {
      return this.$route.query.id || null
    },
    isCreate() {
      return _.isNil(this.id)
    },
    paywall() {
      return paywallStore.get(this.id)
    },
    equity_group_option_s() {
      return equityStore.groupList.map(equity => {
        return {
          text: `${equity.name}`,
          value: equity.id,
        }
      })
    },
    product_option_s() {
      return productStore.list.map(product => {
        let onsale = product.is_onsale ? '在售' : '不在售'
        return {
          text: `${product.name} - ${onsale}`,
          value: product.id,
        }
      })
    },
    payment_channel_option_s() {
      return paymentChannelStore.list.map(channel => {
        let text = `${channel.name}`
        if (channel.note) {
          text += `(${channel.note})`
        }
        return {
          text: text,
          value: channel.id,
        }
      })
    },
    device_type_list() {
      return DEVICE_TYPE_LIST
    },
    form_params() {
      let device_and_channel_config = {}
      DEVICE_TYPE_LIST.forEach(item => {
        let value = this.form.device_channel_id_s[item.device_type]
        device_and_channel_config[item.device_type] = filterNil(value)
      })
      return {
        name: this.form.name,
        alias: this.form.alias,
        intro_md: this.form.intro_md,
        equity_group_id_s: filterNil(this.form.equity_group_id_s),
        product_id_s: filterNil(this.form.product_id_s),
        device_and_channel_config: device_and_channel_config,
      }
    },
  },
  async mounted() {
    await homeStore.doLoad()
    await paywallStore.doLoad()
    await equityStore.doLoad()
    await productStore.doLoad()
    this.syncForm()
  },
  methods: {
    syncForm() {
      if (!this.isCreate && this.paywall) {
        this.form.name = this.paywall.name
        this.form.alias = this.paywall.alias
        this.form.intro_md = this.paywall.intro_md
        this.form.equity_group_id_s = this.paywall.equity_group_id_s || []
        this.form.product_id_s = this.paywall.product_id_s || []
        const channel_config = this.paywall.device_and_channel_config || {}
        DEVICE_TYPE_LIST.forEach(item => {
          let value = channel_config[item.device_type] || []
          this.form.device_channel_id_s[item.device_type] = value
        })
      }
      let defaultOptionOf = (x) => x.slice(0, 1).map(x => x.value)
      if (this.isCreate && this.form.equity_group_id_s.length <= 0) {
        this.form.equity_group_id_s = defaultOptionOf(this.equity_group_option_s)
      }
      if (this.isCreate && this.form.product_id_s.length <= 0) {
        this.form.product_id_s = defaultOptionOf(this.product_option_s)
      }
      DEVICE_TYPE_LIST.forEach(item => {
        let channel_id_s = this.form.device_channel_id_s[item.device_type]
        if (this.isCreate && channel_id_s.length <= 0) {
          let defaultValue = defaultOptionOf(this.payment_channel_option_s)
          this.form.device_channel_id_s[item.device_type] = defaultValue
        }
      })
    },
    async onSave() {
      if (this.isCreate) {
        let paywall = await paywallStore.doCreate(this.form_params)
        this.$router.replace({ query: { id: paywall.id } })
      } else {
        await paywallStore.doUpdate({
          id: this.id,
          ...this.form_params,
        })
        this.syncForm()
      }
      this.$toasted.success('保存成功')
    },
    async onDelete() {
      const msg = `确定要删除付费界面「${this.paywall.name}」吗`
      if (!(await this.$dialog.confirm({ text: msg }))) {
        return
      }
      await paywallStore.doDelete(this.id)
      this.$toasted.success('删除成功')
      this.$router.back()
    },
    async onPreview() {
      let result = await customerStore.doPreviewHomeLink({
        paywall_id: this.id,
      })
      this.previewUrl = result.url
      this.showPreview = true
    },
  },
}
</script>

<style lang="less" scoped>
.row-intro /deep/ textarea {
  font-size: 14px;
  font-family: monospace;
  white-space: pre-wrap;
  line-height: 1.5;
}

.section-payment {
  margin-top: 24px;
}
</style>
